import { useEffect, useMemo } from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { Filter } from "lucide-react";
import { dictionary } from "../../utils/dictionary";
import { useAuth } from "../../context/AuthContext";
import { useWebsitesStore } from "../../store/websitesStore";

interface FunnelOption {
  id: string;
  name: string;
}

interface FunnelFilterProps {
  onFunnelSelect: (websiteId: string, funnelName: string) => void;
}

export const FunnelFilter: React.FC<FunnelFilterProps> = ({ onFunnelSelect }) => {
  const { language } = useAuth();
  const websites = useWebsitesStore((state) => state.websites);
  const isLoading = useWebsitesStore((state) => state.isLoading);
  const initialize = useWebsitesStore((state) => state.initialize);

  useEffect(() => {
    if (websites.length > 0 || isLoading) return;
    void initialize();
  }, [initialize, isLoading, websites.length]);

  const options: FunnelOption[] = useMemo(
    () =>
      websites
        .map((site) => ({
          id: site.websiteId,
          name:
            site.configurations?.name_funnel?.trim() ||
            site.name?.trim() ||
            site.productName?.trim() ||
            site.websiteId,
        }))
        .filter((option) => option.id),
    [websites]
  );

  const getPlaceholder = () =>
    isLoading
      ? dictionary["loading-funnels"]?.[language] || "Cargando..."
      : dictionary["search-funnel"]?.[language] || "Buscar embudo...";

  return (
    <div className="w-full max-w-md">
      <Autocomplete
        id="funnel-filter-autocomplete"
        options={options}
        loading={isLoading}
        getOptionLabel={(option) => option?.name || ""}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_, funnel) => {
          if (funnel) onFunnelSelect(funnel.id, funnel.name);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={getPlaceholder()}
            variant="outlined"
            fullWidth
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <Filter className="w-5 h-5 text-gray-500 mr-2" />
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
              className: "border border-gray-100 rounded-xl bg-white",
              style: {
                color: "black",
                WebkitTextFillColor: "black",
              },
            }}
          />
        )}
        renderOption={(props: React.HTMLAttributes<HTMLLIElement> & { "data-option-index"?: number }, option) => (
          <li
            {...props}
            key={option.id || `funnel-${props["data-option-index"]}`}
            className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
          >
            <div className="flex-1">
              <div className="font-medium text-sm">{option.name}</div>
              <div className="text-xs text-gray-500">{option.id}</div>
            </div>
          </li>
        )}
        noOptionsText={dictionary["no-funnels-found"]?.[language] || "No se encontraron embudos"}
        loadingText={dictionary["loading-funnels"]?.[language] || "Cargando..."}
        className="w-full"
        sx={{
          "& .MuiOutlinedInput-root": {
            height: "48px",
            borderRadius: "0.75rem",
            padding: "0 14px",
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--fluxi-primary-hex, #4368E5)",
              borderWidth: "1px",
            },
          },
          "& .MuiAutocomplete-endAdornment": {
            right: "8px",
          },
          "& .MuiAutocomplete-inputRoot": {
            paddingTop: "0 !important",
            paddingBottom: "0 !important",
            display: "flex",
            alignItems: "center",
          },
          "& .MuiAutocomplete-input": {
            height: "40px",
            padding: "0 !important",
            color: "black",
          },
        }}
      />
    </div>
  );
};

export default FunnelFilter;
